import { FC, memo, useMemo } from 'react';
import {
  useSystemClassifierItemsBy,
  DataObjectRelationsTableRowDto,
  DataObjectRelationVersionType,
  DataObjectType,
  Table,
  VersionTag,
  DataVersionStatus,
  SystemClassifierItemDto,
  ISODate,
  ISODateTime,
  formatDate,
  formatDateTimeShort,
  RelationsTableType,
  TextEllipsis,
} from '@atlas-frontend/sdk';
import { VersionSpan } from '../output-data/VersionCell';
import { createColumnHelper, CellContext } from '@tanstack/react-table';
import { i18nNsFactory } from '@atlas-frontend/i18n';
import { Flex } from 'antd';
import styled from 'styled-components';
import * as Icon from 'react-feather';
import {
  colorYellow,
  colorAccent1,
  colorInfo,
  colorGray,
} from '@atlas-frontend/design-tokens';
import translations from './RelationsTable.translations.json';
import databaseRedIcon from '../../assets/img/database-red.svg';
import { SpinningLoaderIcon } from './SpinningLoaderIcon';
import { RelationsLegendItemIcon } from './RelationsLegend';
import databaseGreenIcon from '../../assets/img/database-green.svg';
import { AppLink } from '../../common/AppLink';
import { path } from '../../paths';
import { withParams } from '../../paths/withParams';
import { buildTimeLink } from './utils';
import { ErrorButton } from '../output-data/ErrorLevel';

const { addTranslations, t } = i18nNsFactory('relations-table');
addTranslations(translations);

const columnHelper = createColumnHelper<DataObjectRelationsTableRowDto>();

interface RelationsTableProps {
  type: RelationsTableType;
  data: DataObjectRelationsTableRowDto[];
}
export const RelationsTable: FC<RelationsTableProps> = ({ data, type }) => {
  const columns = useMemo(
    () => [
      columnHelper.accessor('sequenceNumber', {
        header: () => t('column.sequenceNumber'),
        meta: { width: '8ch' },
        enableSorting: false,
        enableColumnFilter: false,
      }),
      columnHelper.accessor('relationType', {
        header: () => t('column.relationType'),
        meta: { width: '8ch' },
        enableSorting: false,
        enableColumnFilter: false,
      }),
      columnHelper.display({
        id: 'sourceCode',
        header: () => (
          <HeaderWithArrowIcon label={t('column.code')} direction="right" />
        ),
        meta: { width: '24ch' },
        cell: ({ row }) => (
          <CodeCell
            descriptionCode={row.original.sourceDescriptionCodeName}
            groupCode={row.original.sourceGroupCodeName}
            dataObjectType={row.original.sourceDataObjectType as DataObjectType}
          />
        ),
        enableSorting: false,
        enableColumnFilter: false,
      }),
      columnHelper.display({
        id: 'sourceTimeParams',
        header: () => (
          <HeaderWithArrowIcon
            label={t('column.timeParams')}
            direction="right"
          />
        ),
        meta: { width: '20ch' },
        cell: ({ row }) => {
          const {
            sourceDataObjectType,
            sourceGroupId,
            sourceDescriptionId,
            sourceVersion,
            sourceValueDate,
            targetVersion,
          } = row.original;
          return (
            <TimeParamsCell
              frequency={
                row.original.sourceFrequency as SystemClassifierItemDto | null
              }
              valueDate={sourceValueDate}
              time={row.original.sourceTime}
              timeLink={buildTimeLink({
                dataObjectType: sourceDataObjectType,
                groupId: sourceGroupId,
                descriptionId: sourceDescriptionId,
                versionId: sourceVersion?.id ?? null,
                valueDate: sourceValueDate,
                outputVersionId: targetVersion?.id ?? null,
              })}
            />
          );
        },
        enableSorting: false,
        enableColumnFilter: false,
      }),
      columnHelper.accessor('sourceVersion', {
        header: () => (
          <HeaderWithArrowIcon label={t('column.version')} direction="right" />
        ),
        meta: { width: '30ch' },
        cell: RelationVersionCell,
        enableSorting: false,
        enableColumnFilter: false,
      }),
      columnHelper.display({
        id: 'targetCode',
        header: () => (
          <HeaderWithArrowIcon label={t('column.code')} direction="left" />
        ),
        meta: { width: '24ch' },
        cell: ({ row }) => (
          <CodeCell
            descriptionCode={row.original.targetDescriptionCodeName}
            groupCode={row.original.targetGroupCodeName}
            dataObjectType={row.original.targetDataObjectType as DataObjectType}
          />
        ),
        enableSorting: false,
        enableColumnFilter: false,
      }),
      columnHelper.display({
        id: 'targetTimeParams',
        header: () => (
          <HeaderWithArrowIcon
            label={t('column.timeParams')}
            direction="left"
          />
        ),
        meta: { width: '20ch' },
        cell: ({ row }) => {
          const {
            targetDataObjectType,
            targetGroupId,
            targetDescriptionId,
            targetVersion,
            targetValueDate,
            sourceVersion,
          } = row.original;
          return (
            <TimeParamsCell
              frequency={
                row.original.targetFrequency as SystemClassifierItemDto | null
              }
              valueDate={targetValueDate}
              time={row.original.targetTime}
              timeLink={buildTimeLink({
                dataObjectType: targetDataObjectType,
                groupId: targetGroupId,
                descriptionId: targetDescriptionId,
                versionId: targetVersion?.id ?? null,
                valueDate: targetValueDate,
                outputVersionId: sourceVersion?.id ?? null,
              })}
            />
          );
        },
        enableSorting: false,
        enableColumnFilter: false,
      }),
      columnHelper.accessor('targetVersion', {
        header: () => (
          <HeaderWithArrowIcon label={t('column.version')} direction="left" />
        ),
        meta: { width: '30ch' },
        cell: RelationVersionCell,
        enableSorting: false,
        enableColumnFilter: false,
      }),
    ],
    [],
  );

  return (
    <TableContainer>
      <Table columns={columns} data={data} name={`relations-table-${type}`} />
    </TableContainer>
  );
};

interface CodeCellProps {
  descriptionCode: string | null;
  groupCode: string | null;
  dataObjectType: DataObjectType;
}
const CodeCell: FC<CodeCellProps> = memo(
  ({ descriptionCode, groupCode, dataObjectType }) => (
    <Flex vertical gap={2}>
      {groupCode && (
        <TextEllipsis>
          <strong>{t('label.groupCode')}</strong>: {groupCode}
        </TextEllipsis>
      )}
      {descriptionCode && (
        <TextEllipsis>
          <strong>{t(`label.descriptionCode${dataObjectType}`)}</strong>:{' '}
          {descriptionCode}
        </TextEllipsis>
      )}
    </Flex>
  ),
);

interface TimeParamsCellProps {
  frequency: SystemClassifierItemDto | null;
  valueDate: ISODate | null;
  time: ISODateTime | null;
  timeLink?: string | null;
}

const TimeParamsCell: FC<TimeParamsCellProps> = memo(
  ({ frequency, valueDate, time, timeLink }) => {
    const formattedTime = time ? formatDateTimeShort(time) : null;
    return (
      <Flex vertical gap={2}>
        {frequency && (
          <span>
            <strong>{t('label.frequency')}</strong>: {frequency.name}
          </span>
        )}
        {valueDate && (
          <span>
            <strong>{t('label.valueDate')}</strong>: {formatDate(valueDate)}
          </span>
        )}
        {formattedTime && (
          <span>
            <strong>{t('label.time')}</strong>:{' '}
            {timeLink ? (
              <AppLink id="time-link" to={timeLink} target="_blank">
                {formattedTime}
              </AppLink>
            ) : (
              formattedTime
            )}
          </span>
        )}
      </Flex>
    );
  },
);

const RelationVersionCell: FC<
  CellContext<
    DataObjectRelationsTableRowDto,
    DataObjectRelationVersionType | null
  >
> = memo(({ getValue, row, column }) => {
  const version = getValue();
  const isSource = column.id === 'sourceVersion';
  const processingData = isSource
    ? row.original.sourceProcessingData
    : row.original.targetProcessingData;
  const dataObjectType = isSource
    ? row.original.sourceDataObjectType
    : row.original.targetDataObjectType;

  const { PROCESSING_ERROR_LEVEL } = useSystemClassifierItemsBy([
    'PROCESSING_ERROR_LEVEL',
  ]);

  const { hasErrorIcon, hasInfoIcon, errorReportLink } = useMemo(() => {
    const eventId = processingData?.processingEventId;
    if (dataObjectType === DataObjectType.OUT) {
      const hasError = !!processingData?.calculationErrors;
      return {
        hasErrorIcon: hasError,
        hasInfoIcon: false,
        errorReportLink: eventId
          ? withParams(path.outputDataErrorReport, {
              id: eventId,
              type: 'processingEvent',
              processingDataObjectId:
                processingData?.processingDataObjectId ?? undefined,
              errorSegment: hasError
                ? 'calculationErrors'
                : 'missingSourceData',
            })
          : null,
      };
    }
    const levelId = processingData?.validationErrorLevelId;
    if (!levelId)
      return { hasErrorIcon: false, hasInfoIcon: false, errorReportLink: null };
    const code = PROCESSING_ERROR_LEVEL.get(levelId)?.code;
    return {
      hasErrorIcon: code === 'ERROR',
      hasInfoIcon: code === 'INFO',
      errorReportLink: eventId
        ? withParams(path.inputDataErrorReport, { eventId })
        : null,
    };
  }, [processingData, dataObjectType, PROCESSING_ERROR_LEVEL]);
  const hasAccess = processingData?.hasAccess ?? false;

  return (
    <Flex align="center" gap={8}>
      {version?.statusCode != null && (
        <VersionTag className={version.statusCode ?? ''}>
          <Flex gap="small" align="center">
            {version.versionNumber != null && (
              <VersionSpan>
                {`${version.versionNumber}${version.minorNumber ? `.${version.minorNumber}` : ''}`}
              </VersionSpan>
            )}
            <DataVersionStatus status={version.statusCode} />
          </Flex>
        </VersionTag>
      )}
      <Flex align="center" gap={4}>
        {(hasInfoIcon || hasErrorIcon) && (
          <ErrorButton
            id={
              hasErrorIcon
                ? `open-error-report-${row.id}`
                : `open-info-report-${row.id}`
            }
            href={errorReportLink ?? undefined}
            hasAccess={hasAccess}
            navigable={!!errorReportLink}
            icon={
              <Icon.AlertTriangle
                size={18}
                color={hasErrorIcon ? colorAccent1 : colorInfo}
                opacity={hasAccess ? 1 : 0.6}
                style={{ marginTop: 3 }}
              />
            }
          />
        )}
        {processingData?.hasActiveProcessing && (
          <RelationsLegendItemIcon>
            <SpinningLoaderIcon size={18} color={colorGray[7]} />
          </RelationsLegendItemIcon>
        )}
        {processingData?.loadingExists &&
          dataObjectType === DataObjectType.IN && (
            <RelationsLegendItemIcon hasPadding>
              <img src={databaseGreenIcon} alt="database-green-icon" />
            </RelationsLegendItemIcon>
          )}
        {version?.emptyVersion && (
          <RelationsLegendItemIcon hasPadding>
            <img src={databaseRedIcon} alt="database-red-icon" />
          </RelationsLegendItemIcon>
        )}
        {version?.newerVersionAvailable && (
          <RelationsLegendItemIcon>
            <Icon.Database size={18} color={colorYellow[5]} />
          </RelationsLegendItemIcon>
        )}
      </Flex>
    </Flex>
  );
});

const HeaderWithArrowIcon: FC<{
  label: string;
  direction: 'left' | 'right';
}> = ({ label, direction }) => (
  <Flex align="center" gap={4}>
    {label}
    {direction === 'right' ? (
      <Icon.ArrowRight size={14} />
    ) : (
      <Icon.ArrowLeft size={14} />
    )}
  </Flex>
);

const TableContainer = styled.div`
  overflow-x: auto;

  td {
    vertical-align: middle;
  }
`;
